import React, { useContext, useEffect, useState } from 'react';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { Badge, Divider, IconButton, Menu } from '@mui/material';
import { Box } from '@mui/system';
import { Text } from '../elements/text';
import CloseIcon from '@mui/icons-material/Close';
import { CustomButton } from '../elements/button';
import { NOTIFICATION_SUBSCRIPTION } from '../graphql/subscriptions';
import { useSubscription } from '@apollo/client';
import { useSnackbar } from 'notistack';
import { NotificationContext } from '../context/notification-context';
import { GET_ALL_NOTIFICATIONS } from '../graphql/query';
import { MARK_AS_SEEN } from '../graphql/mutation';
import { NotificationOut } from '../types/notification.types';
import { AppContext } from '../context/app-context';
import { useNavigate } from 'react-router-dom';
import { useQueryHook } from '../hooks/use-query.hook';
import { useMutationHook } from '../hooks/use-mutation.hook';

export const Notification = () => {
  const { adminID } = useContext(AppContext);
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const notificationContext = useContext(NotificationContext);

  const TIME_INTERVAL_IN_SECS = 20;
  const { data, fetch: fetchGetAllNotifications } = useQueryHook({
    query: GET_ALL_NOTIFICATIONS,
    variables: {
      payload: {
        filter: {
          subscriberID: adminID?.value,
          seen: false,
        },
      },
    },
    skip: !adminID?.value,
  });

  const { mutate: markAsSeen } = useMutationHook({ mutation: MARK_AS_SEEN });

  const [notifications, setNotifications] = useState<NotificationOut[]>(() => {
    const saved = sessionStorage.getItem('adminNotifications');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    if (data?.getAllNotifications) {
      setNotifications(data.getAllNotifications);
      sessionStorage.setItem(
        'adminNotifications',
        JSON.stringify(data.getAllNotifications),
      );
    }
  }, [data]);

  const reloadNotifications = async (payload = {}, isAutoReload = false) => {
    if (!adminID?.value) return;

    if (isAutoReload) {
      let lastRefreshed = sessionStorage.getItem(
        'adminNotificationsLastRefreshedAt',
      );
      if (!lastRefreshed) {
        sessionStorage.setItem(
          'adminNotificationsLastRefreshedAt',
          `${Date.now()}`,
        );
        lastRefreshed =
          sessionStorage.getItem('adminNotificationsLastRefreshedAt') || '';
      }

      const diffSeconds = Math.floor(
        (Date.now() - parseInt(lastRefreshed)) / 1000,
      );
      if (diffSeconds < TIME_INTERVAL_IN_SECS) return;
    }

    await fetchGetAllNotifications({
      payload: {
        filter: {
          subscriberID: adminID?.value,
          seen: false,
        },
      },
    });

    sessionStorage.setItem(
      'adminNotificationsLastRefreshedAt',
      `${Date.now()}`,
    );
  };

  useEffect(() => {
    const interval = setInterval(async () => {
      await reloadNotifications({}, true);
    }, (TIME_INTERVAL_IN_SECS + 1) * 1000);
    return () => clearInterval(interval);
  }, [adminID?.value]);

  useSubscription(NOTIFICATION_SUBSCRIPTION, {
    variables: {
      subscriberID: adminID?.value,
    },
    onSubscriptionData: (data: any) => {
      const message =
        data?.subscriptionData?.data?.onCreateNotification?.message;
      const body = JSON.parse(
        data?.subscriptionData?.data?.onCreateNotification?.body || '{}',
      );

      enqueueSnackbar(message, {
        autoHideDuration: 5000,
        variant: 'success',
      });

      notificationContext.callSubscribers(body);
      reloadNotifications();
    },
  });

  const onCloseNotificationHandler = async (notification: NotificationOut) => {
    await markAsSeen({
      variables: {
        ids: [notification._id],
      },
    });
    await reloadNotifications();
  };

  const clearAllNotifications = async () => {
    await markAsSeen({
      variables: {
        ids: notifications.map((n: NotificationOut) => n._id),
      },
    });
    await reloadNotifications();
    handleClose();
  };

  const goToOrderPage = async (notification: NotificationOut) => {
    let body = JSON.parse(notification.body || `{}`);
    if (typeof body == 'string' || body instanceof String) {
      body = JSON.parse(body.toString());
    }

    if (notification.type === 'ORDER') {
      navigate('/orders');
      await markAsSeen({
        variables: {
          ids: [notification._id],
        },
      });
      await reloadNotifications();
    }

    setAnchorEl(null);
  };

  const badgeContent = notifications.length > 9 ? '9+' : notifications.length;
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box>
      <IconButton onClick={handleClick}>
        <Badge badgeContent={badgeContent} color="primary">
          <NotificationsActiveIcon
            color={notifications.length === 0 ? 'disabled' : 'action'}
          />
        </Badge>
      </IconButton>

      {notifications.length ? (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          onClose={handleClose}
          open={open}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <Box maxHeight={'500px'} width={500}>
            {notifications.map((notification: NotificationOut) => (
              <Box
                key={notification._id}
                sx={{
                  ':hover': { background: '#ededed' },
                }}
              >
                <Box display="flex" p={2}>
                  <Box
                    onClick={() => goToOrderPage(notification)}
                    width="90%"
                    mt={1}
                    sx={{ cursor: 'pointer', wordBreak: 'break-all' }}
                  >
                    <Text
                      fontWeight={700}
                      fontSize={16}
                      text={notification?.message?.toString()}
                    />
                  </Box>

                  <Box sx={{ cursor: 'pointer' }}>
                    <IconButton
                      onClick={() => onCloseNotificationHandler(notification)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Divider />
              </Box>
            ))}
            <Box p={2}>
              <CustomButton title="Clear All" onClick={clearAllNotifications} />
            </Box>
          </Box>
        </Menu>
      ) : null}
    </Box>
  );
};
